import { GLReportDocType, GLReportLineModel, GLReportModel } from '@common/modelDefinition';
import React from 'react';
import translate from '@data/translations';
import dayjs from 'dayjs';
import useStore from '@data/state/zustand';
import Tooltip from '@components/tooltip/tooltip';
import { formatNumber } from '@components/numberFormat';

export const DocumentHeader = (props: { glReport: GLReportModel | null; from: number; to: number }) => {
    const { glReport, from, to } = props;
    const defaultCurrency: string = useStore((appState: any) => appState.defaultCurrency);
    const locale: string = useStore((appState: any) => appState.locale);

    return (
        <div className="">
            <CompanyHeaderDataForPrit />
            <br />
            <div className="col-12 mt-2 mb-8p">
                <h5 className="mt-2 mb-2 pointer text-center uppercase p-2 color-black">
                    {<b>{translate(glReport?.name)}</b>}{' '}
                </h5>
                <p className="text-center font-12p">
                    {translate('period')}{' '}
                    {dayjs(from)
                        .locale(locale ? locale : 'hr')
                        .format('LL')}
                    . {' - '}{' '}
                    {dayjs(to)
                        .locale(locale ? locale : 'hr')
                        .format('LL')}
                    . {translate('in')} {defaultCurrency}
                </p>
            </div>
        </div>
    );
};

export const CompanyHeaderDataForPrit = () => {
    const companyInfo = useStore.getState().companyInfo;

    return (
        <div className="font-12p">
            <div className="row  mb-8p">
                <div className="col-2">{translate('Company name')}</div>
                <div className="col-3 text-right">{companyInfo?.name}</div>
            </div>
            <div className="row ">
                <div className="col-2">{translate('ID number')}</div>
                <div className="col-3 text-right">{companyInfo?.idNumber}</div>
            </div>
            <div className="row ">
                <div className="col-2">{translate('Address')}</div>
                <div className="col-3 text-right">{companyInfo?.address}</div>
            </div>
            <div className="row ">
                <div className="col-2">{translate('Post office number')}</div>
                <div className="col-3 text-right">
                    {companyInfo?.postOfficeNumber} {companyInfo?.town}
                </div>
            </div>
        </div>
    );
};

interface BilancePrintProps {
    glReport: GLReportModel | null;
    otherSettings: any;
    from: number;
    to: number;
    glReportLines: GLReportLineModel[];
    history: any;
}

export const BalancePrint = (props: BilancePrintProps) => {
    const { glReport, otherSettings, from, to, glReportLines } = props;
    const dateFormat = otherSettings?.dateFormat ? otherSettings.dateFormat : 'DD/MM/YYYY';
    const dateBefore = new Date(from);
    dateBefore.setDate(dateBefore.getDate() - 1);

    const filtered = glReportLines.filter((gl) =>
        typeof gl?.glAccountsList === 'string'
            ? JSON.parse(gl?.glAccountsList)?.length === 1
            : gl?.glAccountsList?.length === 1
    );

    // Pronađi sve minimalne elemente koji su prefiksi drugih elemenata
    const minimalPrefixes = filtered
        .map((item) => item.glAccountsList[0])
        .filter((currentAccount, _, allAccounts) => {
            // Postoji li bilo koji drugi element koji je kraći i počinje sa trenutnim elementom
            return !allAccounts.some((account) => account !== currentAccount && currentAccount.startsWith(account));
        });

    return (
        <table className="calc-table">
            <thead>
                <tr>
                    <th rowSpan={2} className="text-center">
                        {translate('Account')}
                    </th>
                    <th rowSpan={2} className="text-center item-width-lg">
                        {translate('Name')}
                    </th>
                    {glReport?.name === 'trialBalance' ? null : (
                        <th rowSpan={2} className="text-center ">
                            {translate('Expresion')}
                        </th>
                    )}
                    {glReport?.aggregationType === GLReportDocType.sum ? null : (
                        <th className="text-center" colSpan={2}>
                            {translate('Opening state on day')} {dayjs(dateBefore).format(dateFormat)}
                        </th>
                    )}
                    <th className="text-center" colSpan={2}>
                        {translate('Transactions')}
                        {' ('}
                        {dayjs(from).format(dateFormat)}
                        {' - '}
                        {dayjs(to).format(dateFormat)}
                        {') '}
                    </th>
                    <th className="text-center" colSpan={2}>
                        {translate('Closing state on day')} {dayjs(to).format(dateFormat)}
                    </th>
                    <th className="text-center" colSpan={2}>
                        {translate('Balance state on day')} {dayjs(to).format(dateFormat)}
                    </th>
                    {glReport?.name === 'trialBalance' ? null : (
                        <th className="text-center" colSpan={2}>
                            {' '}
                            <Tooltip
                                message={translate('Select balance rule on report definition')}
                                style={{
                                    // position: "absolute",
                                    width: '250px',
                                    top: '20px',
                                    right: '50px',
                                }}
                            >
                                {translate('Closing balance')} {dayjs(to).format(dateFormat)}
                            </Tooltip>
                        </th>
                    )}
                </tr>
                <tr>
                    {glReport?.aggregationType === GLReportDocType.sum ? null : (
                        <>
                            <th>{translate('Debt')}</th>
                            <th>{translate('Credit')}</th>
                        </>
                    )}
                    <th>{translate('Debt')}</th>
                    <th>{translate('Credit')}</th>
                    <th>{translate('Debt')}</th>
                    <th>{translate('Credit')}</th>
                    <th>{translate('Debt')}</th>
                    <th>{translate('Credit')}</th>
                </tr>
            </thead>

            <tbody>
                {glReportLines?.map((gl: GLReportLineModel, index: number) => {
                    const _glAccountsList =
                        typeof gl?.glAccountsList === 'string' ? JSON.parse(gl?.glAccountsList) : gl?.glAccountsList;

                    const _accCumCredit =
                        (gl.lineValue?.openingCredit ? gl.lineValue?.openingCredit : 0) +
                        (gl.lineValue?.credit ? gl.lineValue?.credit : 0);
                    const _accCumDebt =
                        (gl.lineValue?.openingDebt ? gl.lineValue?.openingDebt : 0) +
                        (gl.lineValue?.debt ? gl.lineValue?.debt : 0);

                    const onlyText = _glAccountsList?.length === 0 && !gl.expression;

                    const addBgForTrialBalance =
                        glReport?.name === 'trialBalance'
                            ? _glAccountsList?.length === 1 && minimalPrefixes.includes(_glAccountsList[0])
                                ? true
                                : false
                            : false;

                    return (
                        <tr className={gl?.expression || addBgForTrialBalance ? ' bg-color-light ' : ''} key={index}>
                            <td className="">
                                {_glAccountsList?.map((l: string, index: number) => {
                                    return (
                                        <React.Fragment key={index}>
                                            <span
                                                onClick={() => {
                                                    if (glReport?.name === 'trialBalance') {
                                                        props.history?.push('/glAccountsCards?search=' + l);
                                                    }
                                                }}
                                                className='font-9p'
                                            >
                                                {l}
                                            </span>
                                            {index < _glAccountsList.length - 1 && <span className="mr-2p">,</span>}
                                        </React.Fragment>
                                    );
                                })}
                            </td>
                            <td className="item-width-lg">
                                {gl.lineName} {gl?.columnReference ? <span>({gl?.columnReference})</span> : null}
                            </td>
                            {glReport?.name === 'trialBalance' ? null : (
                                <td className="">{gl?.expression ? ' (' + gl?.expression + ') ' : ''}</td>
                            )}
                            {glReport?.aggregationType === GLReportDocType.sum ? null : (
                                <>
                                    <td className={gl.lineValue?.openingDebt ? ' font-14p ' : ''}>
                                        {onlyText
                                            ? '-'
                                            : gl.lineValue?.openingDebt
                                            ? formatNumber(gl.lineValue?.openingDebt)
                                            : 0}
                                    </td>
                                    <td className={gl.lineValue?.openingCredit ? ' font-14p ' : ''}>
                                        {onlyText
                                            ? '-'
                                            : gl.lineValue?.openingCredit
                                            ? formatNumber(gl.lineValue?.openingCredit)
                                            : 0}
                                    </td>
                                </>
                            )}
                            <>
                                <td className={gl.lineValue?.debt ? ' font-14p ' : ''}>
                                    {onlyText ? '-' : gl.lineValue?.debt ? formatNumber(gl.lineValue?.debt) : 0}
                                </td>
                                <td className={gl.lineValue?.credit ? ' font-14p ' : ''}>
                                    {onlyText ? '-' : gl.lineValue?.credit ? formatNumber(gl.lineValue?.credit) : 0}
                                </td>
                            </>
                            <>
                                <td className={_accCumDebt ? ' font-14p ' : ''}>
                                    {onlyText ? '-' : _accCumDebt ? formatNumber(_accCumDebt) : 0}
                                </td>
                                <td className={_accCumCredit ? ' font-14p ' : ''}>
                                    {onlyText ? '-' : _accCumCredit ? formatNumber(_accCumCredit) : 0}
                                </td>
                            </>
                            <>
                                <td className={gl.lineValue?.debtBalance ? ' font-14p ' : ''}>
                                    {onlyText
                                        ? '-'
                                        : gl.lineValue?.debtBalance && Number(gl.lineValue?.debtBalance) > 0
                                        ? formatNumber(gl.lineValue?.debtBalance)
                                        : 0}
                                </td>
                                <td className={gl.lineValue?.creditBalance ? ' font-14p ' : ''}>
                                    {onlyText
                                        ? '-'
                                        : gl.lineValue?.creditBalance && Number(gl.lineValue?.creditBalance) > 0
                                        ? formatNumber(gl.lineValue?.creditBalance)
                                        : 0}
                                </td>
                            </>
                            {glReport?.name === 'trialBalance' ? null : (
                                <td className={gl.lineValue?.balance ? ' font-14p ' : ''}>
                                    {onlyText ? '-' : gl.lineValue?.balance ? formatNumber(gl.lineValue?.balance) : 0}
                                </td>
                            )}
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
};
